import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { createSearchParams, useNavigate } from "react-router-dom";
import BranchPickerModal from "@components/Doctor/[id]/BranchPickerModal";
import { activeBranches, canBookAtSite } from "@base/modules/appointment/regularBookingRules";
import { ISite } from "@base/modules/site/site.interface";
import siteService from "@base/modules/site/siteService";
import { baseRoutes } from "@base/routes/baseRoutes";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";

/** Hidden only when the clinic's functions are known and do not include booking. */
export const isSiteBookingHidden = (site?: ISite) => typeof site?.special_functions === "string" && !canBookAtSite(site);

/**
 * "Book" on a clinic (spec E5/E6): only clinics offering `SERVICE_MENU`, and the branch is asked
 * first when it has active ones. The booking flow then asks online or at the clinic.
 */
const useSiteBooking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [site, setSite] = useState<ISite>();
  const [isStarting, setIsStarting] = useState<boolean>(false);

  const goToBooking = (code: string, branch?: { code?: string; name?: string }) =>
    navigate({
      pathname: baseRoutes.bookingAppointment,
      search: createSearchParams({
        site_code: code,
        ...(branch?.code ? { site_branch_code: branch.code, site_branch_name: branch.name ?? "" } : {}),
      }).toString(),
    });

  const start = async (target?: ISite) => {
    if (!target?.code) {
      return;
    }

    try {
      setIsStarting(true);
      // The list may not carry the clinic's functions and branches; its own record does.
      const full = await siteService
        .getByCode(target.code)
        .then((resp) => resp.data ?? target)
        .catch(() => target);

      if (isSiteBookingHidden(full)) {
        dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("booking.siteNotBookable") }));
        return;
      }

      if (activeBranches(full).length) {
        setSite(full);
        return;
      }

      goToBooking(target.code);
    } finally {
      setIsStarting(false);
    }
  };

  const branchPicker = site ? (
    <BranchPickerModal
      open
      toggle={() => setSite(undefined)}
      branches={activeBranches(site)}
      onSelect={(branch) => {
        setSite(undefined);
        goToBooking(String(site.code), branch);
      }}
    />
  ) : null;

  return { start, isStarting, branchPicker };
};

export default useSiteBooking;
